/**
 * Offer to travel further and try again.
 *
 * A round whose only problem is the commute limits would otherwise be a dead end:
 * the organizer is told that nobody fits, but not what would fit. The engine
 * computes the smallest uniform limit under which some district becomes
 * reachable, and this turns that number into one explicit action, so widening the
 * limits is a decision the group makes rather than something the engine does
 * silently.
 */

import { useTranslation } from 'react-i18next'

import { Button } from '../ui/button'
import type { ApiError } from '../../lib/api'

export interface RelaxLimitsActionProps {
  error: ApiError | null
  isPending: boolean
  onRelax: (minutes: number) => void
}

/** The suggestion the engine attached to a commute failure, when it has one. */
export function suggestedRelaxation(error: ApiError | null): number | null {
  if (!error || error.code !== 'no_candidates' || error.details.reason !== 'travel_time') {
    return null
  }
  const suggested = error.details.suggestedTravelMinutes
  return typeof suggested === 'number' && Number.isFinite(suggested) ? suggested : null
}

export function RelaxLimitsAction({ error, isPending, onRelax }: RelaxLimitsActionProps) {
  const { t } = useTranslation()
  const suggested = suggestedRelaxation(error)
  if (suggested === null) {
    return null
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-xs">{t('events.relaxHint', { minutes: suggested })}</span>
      <Button
        size="sm"
        variant="outline"
        disabled={isPending}
        onClick={() => onRelax(suggested)}
      >
        {t('events.relaxAction', { minutes: suggested })}
      </Button>
    </div>
  )
}
